import React, { useState } from 'react';
import {
  useGetClassCapacityQuery,
  useUpdateEnrollmentDaysMutation,
} from '../../store/api/localApi';
import ClassDayPicker from '../../components/classes/ClassDayPicker';
import EffectiveFromField, {
  effectiveFromErrorMessage,
} from '../../components/classes/EffectiveFromField';

export interface EditDaysModalProps {
  classId: string;
  className: string;
  /** The class's location, for today's session (the From date's earliest). */
  locationId: string;
  studentId: string;
  studentName: string;
  /**
   * The days the child attends now; the picker starts here. Empty is a legacy
   * all-days row (written by the terms migration): the child attends every day
   * the class runs.
   */
  currentDays: number[];
  /** The row has not started yet: From defaults to its start and cannot precede it. */
  startsOn?: string;
  /** A change already scheduled on this row; a From on or before it replaces it. */
  upcoming?: { from: string; daysOfWeek: number[] };
  onClose: () => void;
}

const sameDays = (a: number[], b: number[]) =>
  a.length === b.length &&
  [...a].sort((x, y) => x - y).join(',') === [...b].sort((x, y) => x - y).join(',');

/**
 * Change the days an enrolled child attends this class. Same day picker as the
 * Enrol dialog, told which days are already the child's own seat so a day that
 * is full only because of them can still be kept or dropped.
 */
const EditDaysModal: React.FC<EditDaysModalProps> = ({
  classId,
  className,
  studentId,
  studentName,
  locationId,
  currentDays,
  startsOn,
  upcoming,
  onClose,
}) => {
  const [updateDays, { isLoading: isSaving }] = useUpdateEnrollmentDaysMutation();
  // Same query the picker runs (RTK shares it): the class's running days are
  // what an empty `currentDays` means, so the admin sees the days the child
  // really attends and a save writes them out explicitly.
  const { data: capacity } = useGetClassCapacityQuery(classId, {
    skip: currentDays.length > 0,
  });
  const shownDays =
    currentDays.length > 0
      ? currentDays
      : [...(capacity?.runningDays ?? [])].sort((a, b) => a - b);
  // null until the admin touches the picker, so the prefill follows the
  // running days once they load.
  const [pickedDays, setDays] = useState<number[] | null>(null);
  const days = pickedDays ?? shownDays;
  const [error, setError] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  // The server's earliest after a too-early refusal (the class started
  // while the dialog was open); the field then floors at it.
  const [serverEarliest, setServerEarliest] = useState<string | undefined>();
  const floors = [startsOn, serverEarliest].filter((d): d is string => !!d).sort();
  const notBefore = floors[floors.length - 1];

  // What the child would attend on the From date without this change: today's
  // days, or — from a scheduled change's date on — the scheduled days. Saving
  // exactly those changes nothing; anything else (including today's days
  // before the scheduled date, which cancels it) is a real change.
  const daysOnFrom =
    upcoming && effectiveFrom && effectiveFrom >= upcoming.from
      ? upcoming.daysOfWeek
      : shownDays;
  const replacesUpcoming = !!upcoming && !!effectiveFrom && effectiveFrom < upcoming.from;
  const canSave =
    !isSaving &&
    days.length > 0 &&
    !!effectiveFrom &&
    (!sameDays(days, daysOnFrom) || replacesUpcoming);

  const save = async () => {
    if (!canSave) return;
    setError('');
    try {
      await updateDays({ classId, studentId, daysOfWeek: days, effectiveFrom }).unwrap();
      onClose();
    } catch (err: any) {
      const dateProblem = effectiveFromErrorMessage(err?.data);
      if (dateProblem) {
        setError(dateProblem.message);
        if (dateProblem.earliest) {
          setServerEarliest(dateProblem.earliest);
          setEffectiveFrom(dateProblem.earliest);
        } else if (dateProblem.suggested) {
          setEffectiveFrom(dateProblem.suggested);
        }
      } else if (err?.data?.error === 'CAPACITY_EXCEEDED') {
        setError('One or more selected days are at capacity. Adjust your selection and try again.');
      } else if (err?.data?.error === 'INVALID_ENROLLMENT_DAYS') {
        setError('Some selected days are outside the class schedule.');
      } else {
        setError(err?.data?.message || err?.data?.error || 'Failed to update days');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="edit-days-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit days — {studentName}</h3>
          {error && (
            <p className="text-sm text-red-600 mb-3" data-testid="enrollment-action-error">
              {error}
            </p>
          )}
          <ClassDayPicker
            classId={classId}
            className={className}
            value={days}
            onChange={setDays}
            disabled={isSaving}
            // The raw days, not the prefill: an all-days row is counted on no
            // day by the capacity summary, so it holds no seat to discount.
            ownDays={currentDays}
          />
          <div className="mt-4">
            <EffectiveFromField
              testId="edit-days-from"
              classId={classId}
              locationId={locationId}
              value={effectiveFrom}
              onChange={setEffectiveFrom}
              notBefore={notBefore}
              upcomingFrom={upcoming?.from}
              disabled={isSaving}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
            <button
              data-testid="edit-days-cancel"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              data-testid="edit-days-save"
              onClick={save}
              disabled={!canSave}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save days'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDaysModal;
